import React, { useRef, useState, useCallback } from 'react';
import UploadFileIcon from '@/components/icons/UploadFileIcon';

export type DropZoneVariant = 'media' | 'file';
export type DropZoneLayout = 'expanded' | 'compact';

export interface DropZoneProps {
  onFilesSelected: (files: File[]) => void;
  accept?: string;
  maxSizeMB?: number;
  label?: string;
  className?: string;
  /** 'media' = image/video (default, light bg, photo icon);
   *  'file'  = PDF/SCORM (purple gradient bg, box icon). */
  variant?: DropZoneVariant;
  /** 'expanded' = big vertical call-to-action (default — used on section/course
   *  image uploads). 'compact' = single-row horizontal strip (used inside
   *  content module editors where vertical real estate is at a premium). */
  layout?: DropZoneLayout;
  /** Show the purple filled "Add" button (triggers file picker). Default true. */
  showAddButton?: boolean;
}

/**
 * GradientDashedBorder — dashed border with a purple→orange gradient rendered
 * as an SVG overlay (the only reliable way to combine radius + dashed + gradient).
 */
function GradientDashedBorder({ radius }: { radius: number }) {
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="dropzone-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style={{ stopColor: 'rgb(var(--color-primary))' }} />
          <stop offset="100%" style={{ stopColor: 'rgb(var(--color-secondary))' }} />
        </linearGradient>
      </defs>
      <rect
        x="1.5"
        y="1.5"
        width="100%"
        height="100%"
        rx={radius}
        ry={radius}
        fill="none"
        stroke="url(#dropzone-grad)"
        strokeWidth={radius >= 16 ? 3 : 2}
        strokeDasharray="4 5"
        style={{ width: 'calc(100% - 3px)', height: 'calc(100% - 3px)' }}
      />
    </svg>
  );
}

export default function DropZone({
  onFilesSelected,
  accept,
  maxSizeMB,
  label = 'Drop or Select File',
  className = '',
  variant = 'media',
  layout = 'expanded',
  showAddButton = false,
}: DropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateAndEmit = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setError(null);

      const valid: File[] = [];
      for (const file of Array.from(files)) {
        if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
          setError(`"${file.name}" exceeds ${maxSizeMB} MB limit.`);
          continue;
        }
        valid.push(file);
      }

      if (valid.length > 0) {
        onFilesSelected(valid);
      }
    },
    [onFilesSelected, maxSizeMB],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      validateAndEmit(e.dataTransfer.files);
    },
    [validateAndEmit],
  );

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    validateAndEmit(e.target.files);
    e.target.value = '';
  };

  if (layout === 'compact') {
    return (
      <CompactLayout
        inputRef={inputRef}
        label={label}
        className={className}
        variant={variant}
        showAddButton={showAddButton}
        isDragging={isDragging}
        error={error}
        maxSizeMB={maxSizeMB}
        accept={accept}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onInputChange={handleInputChange}
      />
    );
  }

  return (
    <ExpandedLayout
      inputRef={inputRef}
      label={label}
      className={className}
      variant={variant}
      showAddButton={showAddButton}
      isDragging={isDragging}
      error={error}
      maxSizeMB={maxSizeMB}
      accept={accept}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onInputChange={handleInputChange}
    />
  );
}

// ─── Layout variants ─────────────────────────────────────────────────────

interface LayoutProps {
  inputRef: React.RefObject<HTMLInputElement>;
  label: string;
  className: string;
  variant: DropZoneVariant;
  showAddButton: boolean;
  isDragging: boolean;
  error: string | null;
  maxSizeMB: number | undefined;
  accept: string | undefined;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function ExpandedLayout({
  inputRef,
  label,
  className,
  variant,
  showAddButton,
  isDragging,
  error,
  maxSizeMB,
  accept,
  onDrop,
  onDragOver,
  onDragLeave,
  onInputChange,
}: LayoutProps) {
  const bgClass =
    variant === 'file'
      ? 'bg-gradient-to-br from-[rgb(var(--color-primary) / 0.12)] via-[rgb(var(--color-secondary) / 0.08)] to-[rgb(var(--color-secondary) / 0.1)] dark:from-primary-500/10 dark:via-primary-500/[0.06] dark:to-primary-500/[0.04]'
      : 'bg-gradient-to-br from-[rgba(255,235,240,0.55)] to-[rgba(255,245,235,0.55)] dark:from-primary-500/10 dark:to-primary-500/[0.04]';

  return (
    <div
      className={`
        relative rounded-2xl
        px-6 py-10 flex flex-col items-center text-center
        transition-[background-color] duration-200 cursor-default
        ${isDragging ? 'bg-primary-500/[0.06]' : bgClass}
        ${className}
      `}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      role="region"
      aria-label="File drop zone"
    >
      <GradientDashedBorder radius={16} />

      <UploadFileIcon className="relative mb-4 pointer-events-none select-none" />

      <p className="relative text-lg font-bold leading-[26px] text-text-primary mb-2">
        {label}
      </p>

      <p className="relative text-sm leading-[22px] text-text-secondary mb-6 max-w-[320px]">
        Drop files here or click to{' '}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="bg-transparent border-none p-0 font-semibold text-primary-500 cursor-pointer underline hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
          style={{ font: 'inherit' }}
        >
          browse
        </button>{' '}
        through your machine.
      </p>

      <div className="relative flex gap-3 flex-wrap justify-center">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-full border border-grey-500/50 bg-surface-elevated/70 px-5 py-2 text-sm font-bold text-text-primary hover:border-primary-500 hover:bg-primary-500/[0.04] transition-[border-color,background-color] duration-150"
        >
          Upload from computer
        </button>

        {showAddButton && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-full border-none px-5 py-2 text-sm font-bold text-white transition-[background-color] duration-150"
            style={{ background: `linear-gradient(to top, ${'rgb(var(--color-primary-700))'}, rgb(var(--color-primary)) 70%)` }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M17.5 19H7a5 5 0 110-10 7 7 0 0110 6.32A3.5 3.5 0 0117.5 19z"
                stroke="#fff"
                strokeWidth="2"
                strokeLinejoin="round"
                fill="none"
              />
              <path d="M12 13v4M10 15h4" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Add
          </button>
        )}
      </div>

      {maxSizeMB && (
        <p className="relative mt-3 text-xs leading-[18px] text-text-disabled">
          Maximum file size: {maxSizeMB} MB
        </p>
      )}

      {error && (
        <p role="alert" className="relative mt-2 text-xs leading-[18px] text-error-500">
          {error}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        aria-hidden="true"
        tabIndex={-1}
        className="hidden"
        onChange={onInputChange}
      />
    </div>
  );
}

function CompactLayout({
  inputRef,
  label,
  className,
  variant,
  showAddButton,
  isDragging,
  error,
  maxSizeMB,
  accept,
  onDrop,
  onDragOver,
  onDragLeave,
  onInputChange,
}: LayoutProps) {
  const bgClass =
    variant === 'file'
      ? 'bg-primary-500/[0.06]'
      : 'bg-primary-500/[0.03]';

  return (
    <div
      className={`
        relative rounded-[14px] transition-[background-color] duration-200 [transition-timing-function:cubic-bezier(0.23,1,0.32,1)]
        ${isDragging ? 'bg-primary-500/[0.08]' : bgClass}
        ${className}
      `}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      role="region"
      aria-label="File drop zone"
    >
      <GradientDashedBorder radius={14} />

      <div className="relative flex items-center gap-4 px-4 py-3.5">
        <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl bg-primary-500/10 text-primary-500">
          <UploadFileIcon className="w-6 h-6 pointer-events-none select-none" />
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <p className="font-sans font-semibold text-[13px] leading-5 text-text-primary truncate">
            {label}
          </p>
          <p className="font-sans text-[12px] leading-4 text-text-secondary">
            Drop here or{' '}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="bg-transparent border-0 p-0 font-semibold text-primary-500 cursor-pointer underline-offset-2 hover:underline transition-colors"
              style={{ font: 'inherit' }}
            >
              browse
            </button>
            {maxSizeMB && <span className="text-text-disabled"> · max {maxSizeMB} MB</span>}
          </p>
        </div>

        {showAddButton && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full border-0 px-4 h-9 font-sans font-bold text-[13px] text-white cursor-pointer transition-[transform,filter] duration-150 [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] hover:brightness-110 active:scale-[0.97]"
            style={{ background: `linear-gradient(to top, ${'rgb(var(--color-primary-700))'}, rgb(var(--color-primary)) 70%)` }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
            Add
          </button>
        )}
      </div>

      {error && (
        <p
          role="alert"
          className="relative mx-4 mb-3 -mt-1 text-[11px] leading-4 text-error-500"
        >
          {error}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        aria-hidden="true"
        tabIndex={-1}
        className="hidden"
        onChange={onInputChange}
      />
    </div>
  );
}
